import debounce from "lodash.debounce";

export function memoizeAsync<TArg, TResult>(
    fn: (arg: TArg) => Promise<TResult>,
    getKey: (arg: TArg) => string
): (arg: TArg) => Promise<TResult> {
    const cache: Map<string, TResult> = new Map<string, TResult>();

    return async (arg: TArg): Promise<TResult> => {
        const key = getKey(arg);
        const storedResult = cache.get(key);
        if (storedResult != undefined) {
            return storedResult;
        }

        const result = await fn(arg);
        cache.set(key, result);
        return result;
    };
}

function delay(timeout: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, timeout);
    });
}

export class MergedRequestsDeferred<TArg, TResult> {
    public arg: TArg;
    public readonly promise: Promise<TResult>;
    public resolve!: (result: TResult) => void;
    public reject!: (error: unknown) => void;

    public constructor(arg: TArg) {
        this.arg = arg;
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}

export type MergedRequests<TArg, T> = Array<MergedRequestsDeferred<TArg, T>>;

export function retryAsync<TArg, TResult>(
    fn: (arg: MergedRequests<TArg, TResult>) => Promise<void>,
    interval: number
): (arg: MergedRequests<TArg, TResult>) => Promise<void> {
    return async (args: MergedRequests<TArg, TResult>): Promise<void> => {
        try {
            await fn(args);
        } catch {
            try {
                await delay(interval);
                await fn(args);
            } catch (error) {
                for (const arg of args) {
                    arg.reject(error);
                }
            }
        }
    };
}

export function debouncedMerge<TArg, TResult>(
    fn: (requests: MergedRequests<TArg, TResult>) => void,
    interval: number,
    getArgKey: (arg: TArg) => string
): (input: TArg) => Promise<TResult> {
    let pendingRequests = new Map<string, MergedRequestsDeferred<TArg, TResult>>();

    const debouncedFn = debounce(() => {
        fn([...pendingRequests.values()]);
        pendingRequests = new Map<string, MergedRequestsDeferred<TArg, TResult>>();
    }, interval);

    return (arg: TArg): Promise<TResult> => {
        const key = getArgKey(arg);
        let request = pendingRequests.get(key);
        if (request != undefined) {
            return request.promise;
        }
        request = new MergedRequestsDeferred<TArg, TResult>(arg);
        pendingRequests.set(key, request);
        debouncedFn();
        return request.promise;
    };
}
